import { ReactElement } from "react";

import { Center, Skeleton, Stack } from "@chakra-ui/react";

import { blankContract } from "__fixtures__/contracts";
import Empty, { EmptyVariants } from "components/Empty";
import PrimaryButton from "components/PrimaryButton";
import { useRequestParams } from "contexts/RequestParamsProvider";
import { dropShadowCardSize } from "theme/foundations/shadows";
import { mobileStyleBreakpoint } from "util/styleHelpers";
import { StandardContractCard } from "./ContractCard";

const LOADING_LINES = 4;

const skeletonContractsList = new Array(LOADING_LINES).fill(blankContract);

const contractsListSpacing = mobileStyleBreakpoint(4, 6);

const ContractsList = ({
  contracts,
  onViewDetails,
}: {
  contracts?: Contract[];
  onViewDetails(docEntry: number): void;
}): ReactElement => {
  const { hasSearchTerm, hasSelectedFilters, clearSearchTerm, clearFilters } =
    useRequestParams();

  const emptyButtonOnClick = hasSelectedFilters
    ? clearFilters
    : clearSearchTerm;
  const emptyButtonText = `Clear ${
    hasSelectedFilters ? "all filters" : "search"
  }`;

  let emptyVariant: EmptyVariants;
  if (hasSelectedFilters) {
    emptyVariant = "filter";
  } else if (hasSearchTerm) {
    emptyVariant = "search";
  }

  return !contracts || (contracts && contracts.length > 0) ? (
    <Stack spacing={contractsListSpacing} p={dropShadowCardSize}>
      {(contracts ?? skeletonContractsList).map((contract, key) => (
        <Skeleton isLoaded={!!contracts} key={key}>
          <StandardContractCard
            data-testid="contractsList"
            contract={contract}
            onViewDetails={onViewDetails}
          />
        </Skeleton>
      ))}
    </Stack>
  ) : (
    <Center>
      <Empty
        title={
          hasSelectedFilters || hasSearchTerm
            ? "No results found"
            : "No contracts yet"
        }
        maxWidth="400px"
        message="Once approved, contracts will appear here."
        variant={emptyVariant}
      >
        {(hasSelectedFilters || hasSearchTerm) && (
          <PrimaryButton
            colorScheme="grey"
            onClick={emptyButtonOnClick}
            w="100%"
          >
            {emptyButtonText}
          </PrimaryButton>
        )}
      </Empty>
    </Center>
  );
};

export default ContractsList;
